<script>
import Button from '@/components/Button';
import InputComponent from '@/components/InputComponent';
import { mapState } from 'vuex';
import { addNotification, clearNotification } from '@/utils/notification';
import { amountFilter } from '../../helpers/filters';

export default {
  name: 'Exchange',
  components: {
    Button,
    InputComponent,
  },
  props: {
    data: {
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
    saleId: {
      type: String,
    },
  },
  data: () => ({
    fromAmount: 0,
    toAmount: 0,
    loading: false,
  }),
  created() {
    this.$root.$on('showExchange', this.showExchange);
  },
  beforeDestroy() {
    this.$root.$off('showExchange', this.showExchange);
  },
  computed: {
    ...mapState({
      ethAddress: (state) => state.User.ethAddress,
      chainName: (state) => state.User.chainName,
    }),
    getArrow() {
      // eslint-disable-next-line global-require,import/no-dynamic-require
      return require(`../../assets/img/${this.chainName}/launchpad/arrow.svg`);
    },
  },
  methods: {
    amountFilter,
    showExchange() {
      this.show = true;
    },
    maximize() {
      this.fromAmount = this.$BN(this.data.leftToBuy).lte(this.data.tokenBalance)
        ? this.data.leftToBuy
        : this.data.tokenBalance;
    },
    validateAmount() {
      const nAmount = this.fromAmount;
      const maxBalance = this.data.tokenBalance;
      const maxLeftToBuy = this.data.leftToBuy;
      if (this.$BN(maxBalance).gte(maxLeftToBuy) && this.$BN(nAmount).gt(maxLeftToBuy)) {
        this.fromAmount = maxLeftToBuy;
      } else if (
        this.$BN(maxBalance).lt(maxLeftToBuy)
        && (this.$BN(nAmount).gt(maxLeftToBuy) || this.$BN(nAmount).gt(maxBalance))
      ) {
        this.fromAmount = maxBalance;
      }
    },
    async registerSale() {
      if (!this.$BN(this.fromAmount).gt(0)) return false;
      try {
        this.loading = true;
        const amount = this.$contracts
          .toWei(String(this.fromAmount))
          .toFixed(0)
          .toString();

        await this.$contracts
          .getContract('launchpadSale')
          .methods.registerSale(this.saleId, amount)
          .send({ from: this.ethAddress })
          .once('transactionHash', (hash) => {
            addNotification(
              {
                type: 'info',
                title: 'Pending sale:',
                data: { hash },
              },
              { root: true },
            );
          })
          .once('confirmation', () => {
            clearNotification('info');
            addNotification(
              {
                type: 'success',
                title: 'Successful sale!',
                config: { duration: 15000 },
              },
              { root: true },
            );
            this.loading = false;
            this.fromAmount = 0;
            this.$emit('update:show');
          })
          .once('error', () => {
            clearNotification('info');
            addNotification(
              {
                type: 'warn',
                title: 'Sale failed',
                config: { duration: 5000 },
              },
              { root: true },
            );
            this.loading = false;
          });
        return true;
      } catch (e) {
        this.loading = false;
        return false;
      }
    },
  },
};
</script>

<template>
  <div>
    <v-dialog @input="$emit('update:show')" :value="show" overlay-opacity="0.8">
      <div class="main-block exchange" v-if="show">
        <div class="modal__input">
          <InputComponent
            @change="validateAmount"
            v-model="fromAmount"
            @click="maximize"
            class="mb-5"
            :max="true"
          >
            <template v-slot:label>
              <div class="d-flex justify-space-between exchange__input">
                <span>{{ $t('exchange.from') }}</span>
                <span class="text--grey">
                  {{ $t('exchange.balance') }}
                  {{ $BN(data.tokenBalance) | amount(4) }}
                  {{ data.paymentToken }}
                </span>
              </div>
            </template>
            <template v-slot:input>
              <span class="input-token-name">{{ data.paymentTokenName }}</span>
            </template>
          </InputComponent>
        </div>
        <div class="d-flex justify-center">
          <img :src="getArrow" class="arrow" />
        </div>
        <div class="modal__input modal__input--to">
          <InputComponent disabled :value="amountFilter($BN(fromAmount / data.tokenPrice), 4)">
            <template v-slot:label>
              <div class="d-flex justify-space-between exchange__input">
                <span>{{ $t('exchange.to') }}</span>
                <span class="text--grey">
                  {{
                    $t('exchange.remain', {
                      sum: amountFilter($BN(data.leftToBuy / data.tokenPrice), 4),
                    })
                  }}
                </span>
              </div>
            </template>
            <template v-slot:input>
              <span class="input-token-name">{{ data.tokenName }}</span>
            </template>
          </InputComponent>
        </div>
        <p class="text-center mb-0 allocation">
          {{ $t('exchange.allocation') }}
          {{ data.stakeAllocationAmount | amount(18) }}
          {{ data.paymentTokenName }}
        </p>
        <div class="modal__btns mt-3">
          <Button @click="registerSale" :disabled="loading" :loading="loading">
            {{ $t('exchange.button') }}
          </Button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<style lang="scss">
.exchange {
  padding: 4.5rem;

  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.15), inset 1px 2px 2px rgba(255, 255, 255, 0.5),
    inset -1px -1px 2px rgba(221, 102, 102, 0.13);
  background: linear-gradient(103.94deg, #fdfafb -1.71%, #f7fbfc 45.51%);

  .arrow {
    width: 1.74rem;
  }

  .allocation {
    font-size: 1.6rem;
    font-weight: bold;
    color: $grey-font-color;

    body.binance & {
      color: $b-grey-font-color;
    }
  }

  .modal__btns .btn {
    padding: 1.35em 7em;
  }

  &__input {
    padding: 0 2.5rem;

    span {
      font-size: 1.8rem;
      font-weight: 600;
      color: $main-font-color;

      body.binance & {
        color: $b-grey-font-color;
      }
    }
  }

  .modal__input--to .input-max {
    top: 1.8rem;
    right: 2rem;
  }

  .input__wrapper .input-max {
    display: flex;
    align-items: center;

    .btn.btn--round {
      font-size: 1.4rem;
      font-weight: 600;
    }
  }

  .input__wrapper .input-token-name {
    font-size: 2.2rem;
    font-weight: 600;
    color: #ff9d9d;

    body.binance & {
      color: $b-main-font-color;
    }
  }
}
</style>
